import {IncomingMessage} from 'http';
import {STRIPE_ENV} from 'mkt-ssr/lib/env';

export const ValidHosts = ['stripe.com', 'mkt.corp.stripe.com'] as const;

const NAUTILUS_SERVICES = {
  WORKER: 'mkt',
  SSR: 'mkt_ssr',
};

const CDN_MAP = {
  [STRIPE_ENV.PROD]: 'b.stripecdn.com',
  [STRIPE_ENV.PREPROD]: 'preprod-b.stripecdn.com',
  [STRIPE_ENV.QA]: 'qa-b.stripecdn.com',
} as Record<string, string>;

export type ValidHost = (typeof ValidHosts)[number];

export interface HostData {
  resolved: ValidHost;
  requested: string;
}

function isValidHost(host: string): host is ValidHost {
  return ValidHosts.some((x) => x === host);
}

export function getHostData(req?: IncomingMessage): HostData {
  let resolved: ValidHost;
  const requestResolved =
    req?.headers['x-mkt-resolved-host']?.toString() || 'stripe.com';

  if (isValidHost(requestResolved)) {
    resolved = requestResolved;
  } else {
    throw new Error(`${requestResolved} is not a valid host!`);
  }

  return {
    requested: req?.headers['x-mkt-requested-host']?.toString() || 'stripe.com',
    resolved,
  };
}

export function isDevboxRequest(host: string, service: string) {
  return host.includes(`${service}-`);
}

export function rewriteDevboxServiceHost(
  currentHost: string,
  fromService: string,
  toService: string,
) {
  return currentHost.replace(`${fromService}-`, `${toService}-`) || '';
}

export function getCdnHostForEnvironment(hostData: HostData, env: STRIPE_ENV) {
  const cdnHost = CDN_MAP[env] || '';
  if (cdnHost) {
    return `https://${cdnHost}`;
  }

  // There's no CDN in dev -- instead, we rewrite requests from the
  // ruby worker host to hit the SSR service directly
  if (
    env === STRIPE_ENV.DEV &&
    isDevboxRequest(hostData.requested, NAUTILUS_SERVICES.WORKER)
  ) {
    const ssrHost = rewriteDevboxServiceHost(
      hostData.requested,
      NAUTILUS_SERVICES.WORKER,
      NAUTILUS_SERVICES.SSR,
    );
    return `https://${ssrHost}`;
  }

  // Otherwise serve directly from app (e.g. when hitting ssr service directly)
  return '';
}
